import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from '@tanstack/react-router';
import { ContentBlockType } from '@onesource/schemas';
import type { TContentBlockBase } from '@onesource/schemas';
import { useGetUserAttempts, type TQuizAttempt } from '@onesource/services';

type FilterType = 'all' | 'live_class' | 'quiz' | 'reading';

interface LessonsSectionProps {
  contentBlocks: TContentBlockBase[];
  lessonId: string;
}

/**
 * Live Badge Component - Shows "LIVE" badge with red dot
 */
const LiveBadge = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#e3f2fd',
      color: '#1976d2',
      borderRadius: 1,
      px: 1,
      py: 0.5,
      fontWeight: 'bold',
      fontSize: '0.75rem',
      minWidth: 50,
    }}
  >
    LIVE
  </Box>
);

/**
 * Quiz Icon Badge Component
 */
const QuizBadge = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#fff3e0',
      borderRadius: 1,
      p: 0.5,
      minWidth: 50,
    }}
  >
    <QuizOutlinedIcon sx={{ color: '#ff9800', fontSize: 28 }} />
  </Box>
);

/**
 * Quiz Item Component - Handles quiz block display with attempt data
 */
const QuizItem = ({
  block,
  onNavigate,
}: {
  block: TContentBlockBase;
  onNavigate: (path: string, params?: { id: string }) => void;
}) => {
  const { data: attempts, isLoading } = useGetUserAttempts(block.quizId || '');

  // Find best graded attempt
  const gradedAttempts = attempts?.filter((a: TQuizAttempt) => a.status === 'graded') || [];
  const bestAttempt =
    gradedAttempts.length > 0
      ? gradedAttempts.reduce((best: TQuizAttempt, current: TQuizAttempt) =>
          (current.scorePercent || 0) > (best.scorePercent || 0) ? current : best,
        )
      : null;

  const hasAttempt = (attempts?.length || 0) > 0;
  const isCompleted = bestAttempt !== null;

  const handleClick = () => {
    if (bestAttempt) {
      onNavigate('/community-hub/attempts/$id', { id: bestAttempt.id });
    } else if (block.quizId) {
      onNavigate('/community-hub/quizzes/$id', { id: block.quizId });
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        '&:hover': {
          backgroundColor: 'grey.50',
        },
      }}
    >
      {/* Completion Status */}
      <Box sx={{ pt: 0.5 }}>
        {isCompleted ? (
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
        ) : (
          <RadioButtonUncheckedIcon sx={{ color: 'grey.400', fontSize: 24 }} />
        )}
      </Box>

      {/* Icon */}
      <QuizBadge />

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Type Badge and Info */}
        <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
          <Chip
            label="Quiz"
            size="small"
            sx={{
              backgroundColor: '#fff3e0',
              color: '#e65100',
              fontWeight: 500,
              fontSize: '0.75rem',
            }}
          />
          {typeof block.metadata?.dateRange === 'string' ? (
            <Typography variant="caption" color="text.secondary">
              {block.metadata.dateRange}
            </Typography>
          ) : null}
          {!isLoading && !hasAttempt && (
            <Typography variant="caption" color="text.secondary">
              Score will be added
            </Typography>
          )}
        </Box>

        {/* Title */}
        <Typography variant="body1" fontWeight={500} sx={{ mb: 0.5 }}>
          {block.title || 'Quiz'}
        </Typography>

        {/* Notes */}
        {typeof block.metadata?.notes === 'string' ? (
          <Typography variant="body2" color="text.secondary">
            Notes: {block.metadata.notes}
          </Typography>
        ) : null}
      </Box>

      {/* Score and Action */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        {bestAttempt && (
          <Typography
            variant="body2"
            sx={{
              color: 'primary.main',
              fontWeight: 500,
            }}
          >
            Mark {bestAttempt.earnedPoints}/{bestAttempt.totalPoints}
          </Typography>
        )}
        <Button
          variant="outlined"
          size="small"
          endIcon={<ArrowForwardIcon />}
          onClick={handleClick}
          disabled={!block.quizId}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {hasAttempt ? 'RESULT' : 'START'}
        </Button>
      </Box>
    </Paper>
  );
};

/**
 * Live Class Item Component - Handles vimeo block display
 */
const LiveClassItem = ({
  block,
  onNavigateToVideo,
}: {
  block: TContentBlockBase;
  onNavigateToVideo: (block: TContentBlockBase) => void;
}) => {
  // For vimeo blocks, we consider them "completed" if they've been viewed
  // For now, we'll show them as available
  const isCompleted = false;

  const formatDuration = (seconds: number | null) => {
    if (!seconds) {
      return null;
    }
    const mins = Math.floor(seconds / 60);
    const hrs = Math.floor(mins / 60);
    if (hrs > 0) {
      return `${hrs}h ${mins % 60}m`;
    }
    return `${mins}m`;
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        '&:hover': {
          backgroundColor: 'grey.50',
        },
      }}
    >
      {/* Completion Status */}
      <Box sx={{ pt: 0.5 }}>
        {isCompleted ? (
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
        ) : (
          <RadioButtonUncheckedIcon sx={{ color: 'grey.400', fontSize: 24 }} />
        )}
      </Box>

      {/* Icon */}
      <LiveBadge />

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Type Badge and Info */}
        <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
          <Chip
            label="Live Class"
            size="small"
            sx={{
              backgroundColor: '#e3f2fd',
              color: '#1565c0',
              fontWeight: 500,
              fontSize: '0.75rem',
            }}
          />
          {block.vimeoDurationSeconds && (
            <Typography variant="caption" color="text.secondary">
              {formatDuration(block.vimeoDurationSeconds)}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Recording available
          </Typography>
        </Box>

        {/* Title */}
        <Typography variant="body1" fontWeight={500}>
          {block.title || 'Live Class Recording'}
        </Typography>
      </Box>

      {/* Action */}
      <Box sx={{ flexShrink: 0 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PlayCircleOutlineIcon />}
          onClick={() => onNavigateToVideo(block)}
          sx={{ whiteSpace: 'nowrap' }}
        >
          CLASS RECORDINGS
        </Button>
      </Box>
    </Paper>
  );
};

/**
 * Resource Item Component - Handles PDF and other resource blocks
 */
const ResourceItem = ({
  block,
  onNavigateToResource,
}: {
  block: TContentBlockBase;
  onNavigateToResource: (block: TContentBlockBase) => void;
}) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        '&:hover': {
          backgroundColor: 'grey.50',
        },
      }}
    >
      {/* Completion Status */}
      <Box sx={{ pt: 0.5 }}>
        <RadioButtonUncheckedIcon sx={{ color: 'grey.400', fontSize: 24 }} />
      </Box>

      {/* Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fafafa',
          borderRadius: 1,
          p: 0.5,
          minWidth: 50,
        }}
      >
        <PictureAsPdfOutlinedIcon sx={{ color: '#d32f2f', fontSize: 28 }} />
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Type Badge */}
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <Chip
            label="Resource"
            size="small"
            sx={{
              backgroundColor: '#fafafa',
              color: '#616161',
              fontWeight: 500,
              fontSize: '0.75rem',
            }}
          />
          {block.pdfFilename && (
            <Typography variant="caption" color="text.secondary">
              {block.pdfFilename}
            </Typography>
          )}
        </Box>

        {/* Title */}
        <Typography variant="body1" fontWeight={500}>
          {block.title || 'Resource'}
        </Typography>
      </Box>

      {/* Action */}
      <Box sx={{ flexShrink: 0 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onNavigateToResource(block)}
          sx={{ whiteSpace: 'nowrap' }}
        >
          VIEW
        </Button>
      </Box>
    </Paper>
  );
};

/**
 * Text Item Component - Handles TEXT content blocks
 */
const TextItem = ({
  block,
  onNavigateToText,
}: {
  block: TContentBlockBase;
  onNavigateToText: (block: TContentBlockBase) => void;
}) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        '&:hover': {
          backgroundColor: 'grey.50',
        },
      }}
    >
      {/* Completion Status */}
      <Box sx={{ pt: 0.5 }}>
        <RadioButtonUncheckedIcon sx={{ color: 'grey.400', fontSize: 24 }} />
      </Box>

      {/* Icon - Purple theme for reading */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f3e5f5',
          borderRadius: 1,
          p: 0.5,
          minWidth: 50,
        }}
      >
        <ArticleOutlinedIcon sx={{ color: '#7b1fa2', fontSize: 28 }} />
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Type Badge */}
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <Chip
            label="Reading"
            size="small"
            sx={{
              backgroundColor: '#f3e5f5',
              color: '#7b1fa2',
              fontWeight: 500,
              fontSize: '0.75rem',
            }}
          />
        </Box>

        {/* Title */}
        <Typography variant="body1" fontWeight={500}>
          {block.title || 'Text Content'}
        </Typography>

        {/* Body preview */}
        {block.body && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              mt: 0.5,
            }}
          >
            {block.body}
          </Typography>
        )}
      </Box>

      {/* Action */}
      <Box sx={{ flexShrink: 0 }}>
        <Button
          variant="outlined"
          size="small"
          endIcon={<ArrowForwardIcon />}
          onClick={() => onNavigateToText(block)}
          sx={{ whiteSpace: 'nowrap' }}
        >
          READ
        </Button>
      </Box>
    </Paper>
  );
};

/**
 * Lessons Section Component
 * Displays content items with filter tabs, completion status, and action buttons
 */
export const LessonsSection = ({ contentBlocks, lessonId }: LessonsSectionProps) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>('all');

  // Filter blocks to only show interactive content (text, vimeo, quiz, pdf)
  const interactiveBlocks = contentBlocks.filter(
    block =>
      block.blockType === ContentBlockType.TEXT ||
      block.blockType === ContentBlockType.VIMEO ||
      block.blockType === ContentBlockType.QUIZ ||
      block.blockType === ContentBlockType.PDF,
  );

  // Apply filter
  const filteredBlocks = interactiveBlocks.filter(block => {
    if (filter === 'all') {
      return true;
    }
    if (filter === 'live_class') {
      return block.blockType === ContentBlockType.VIMEO;
    }
    if (filter === 'quiz') {
      return block.blockType === ContentBlockType.QUIZ;
    }
    if (filter === 'reading') {
      return block.blockType === ContentBlockType.TEXT;
    }
    return true;
  });

  // Count items by type
  const liveClassCount = interactiveBlocks.filter(b => b.blockType === ContentBlockType.VIMEO).length;
  const quizCount = interactiveBlocks.filter(b => b.blockType === ContentBlockType.QUIZ).length;
  const readingCount = interactiveBlocks.filter(b => b.blockType === ContentBlockType.TEXT).length;

  // Quiz navigation handler
  const handleNavigate = (path: string, params?: { id: string }) => {
    if (path === '/community-hub/attempts/$id' && params) {
      navigate({ to: '/community-hub/attempts/$id', params: { id: params.id } });
    } else if (path === '/community-hub/quizzes/$id' && params) {
      navigate({ to: '/community-hub/quizzes/$id', params: { id: params.id } });
    } else {
      navigate({ to: '/community-hub' });
    }
  };

  // Video navigation handler - navigates to dedicated video page with block data
  const handleNavigateToVideo = (block: TContentBlockBase) => {
    navigate({
      to: '/community-hub/videos/$id',
      params: { id: block.id },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      state: { block, lessonId } as any,
    });
  };

  // Resource navigation handler - navigates to dedicated resource page with block data
  const handleNavigateToResource = (block: TContentBlockBase) => {
    navigate({
      to: '/community-hub/resources/$id',
      params: { id: block.id },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      state: { block, lessonId } as any,
    });
  };

  // Text navigation handler - navigates to dedicated text reader page with block data
  const handleNavigateToText = (block: TContentBlockBase) => {
    navigate({
      to: '/community-hub/text/$id',
      params: { id: block.id },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      state: { block, lessonId } as any,
    });
  };

  // Don't render if no interactive blocks
  if (interactiveBlocks.length === 0) {
    return null;
  }

  return (
    <Box>
      {/* Header */}
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Lessons
      </Typography>

      {/* Filter Tabs */}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, newFilter) => newFilter && setFilter(newFilter)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: 2,
              py: 0.5,
              borderRadius: '20px !important',
              border: '1px solid',
              borderColor: 'divider',
              mr: 1,
              '&.Mui-selected': {
                backgroundColor: 'grey.900',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'grey.800',
                },
              },
            },
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          {liveClassCount > 0 && <ToggleButton value="live_class">Live Class</ToggleButton>}
          {quizCount > 0 && <ToggleButton value="quiz">Quiz</ToggleButton>}
          {readingCount > 0 && <ToggleButton value="reading">Reading</ToggleButton>}
        </ToggleButtonGroup>
      </Box>

      {/* Content Items */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {filteredBlocks.map(block => {
          if (block.blockType === ContentBlockType.QUIZ) {
            return <QuizItem key={block.id} block={block} onNavigate={handleNavigate} />;
          }
          if (block.blockType === ContentBlockType.VIMEO) {
            return <LiveClassItem key={block.id} block={block} onNavigateToVideo={handleNavigateToVideo} />;
          }
          if (block.blockType === ContentBlockType.PDF) {
            return <ResourceItem key={block.id} block={block} onNavigateToResource={handleNavigateToResource} />;
          }
          if (block.blockType === ContentBlockType.TEXT) {
            return <TextItem key={block.id} block={block} onNavigateToText={handleNavigateToText} />;
          }
          return null;
        })}
      </Box>

      {/* Empty State */}
      {filteredBlocks.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No{' '}
            {filter === 'live_class'
              ? 'live classes'
              : filter === 'quiz'
                ? 'quizzes'
                : filter === 'reading'
                  ? 'reading content'
                  : 'items'}{' '}
            found
          </Typography>
        </Paper>
      )}
    </Box>
  );
};
